// Revenue dashboard wire contract (#602). Admin-only finance dashboard
// fed from a public Google Sheets workbook through the backend; the
// browser never fetches the sheet directly. Manager-level financial
// breakdowns travel inside the response, so the controller MUST attach
// a private `Cache-Control` header and the schema MUST stay closed
// (`.strict()`) so a future drift in upstream parsing does not leak
// unexpected fields onto the wire.
import { z } from 'zod';

/**
 * @aiPurpose ISO month key in the form `YYYY-MM` with months `01..12`.
 *   The dashboard query parameter, every cache key, and the wire field
 *   `selectedMonth.key` all use this exact shape so the frontend can
 *   round-trip a month identifier without re-deriving from Russian
 *   sheet labels.
 * @aiInvariant validation is strict — invalid strings return 400 with
 *   `REVENUE_INVALID_MONTH` rather than silently coercing to a default.
 */
export const RevenueMonthKeySchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected ISO month key YYYY-MM');
export type RevenueMonthKey = z.infer<typeof RevenueMonthKeySchema>;

export const RevenueCurrencySchema = z.literal('RUB');
export type RevenueCurrency = z.infer<typeof RevenueCurrencySchema>;

export const RevenueCategorySchema = z.enum(['buyouts', 'foreignTrade', 'logistics']);
export type RevenueCategory = z.infer<typeof RevenueCategorySchema>;

export const RevenueCategoryBreakdownSchema = z
  .object({
    buyouts: z.number().nonnegative(),
    foreignTrade: z.number().nonnegative(),
    logistics: z.number().nonnegative(),
  })
  .strict();
export type RevenueCategoryBreakdown = z.infer<typeof RevenueCategoryBreakdownSchema>;

export const RevenueMonthDescriptorSchema = z
  .object({
    key: RevenueMonthKeySchema,
    label: z.string().min(1),
    sheetName: z.string().min(1),
  })
  .strict();
export type RevenueMonthDescriptor = z.infer<typeof RevenueMonthDescriptorSchema>;

export const RevenueCumulativePointSchema = z
  .object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected ISO date YYYY-MM-DD'),
    total: z.number().nonnegative(),
  })
  .strict();
export type RevenueCumulativePoint = z.infer<typeof RevenueCumulativePointSchema>;

export const RevenueManagerRowSchema = z
  .object({
    name: z.string().min(1),
    total: z.number().nonnegative(),
    buyouts: z.number().nonnegative(),
    foreignTrade: z.number().nonnegative(),
    logistics: z.number().nonnegative(),
  })
  .strict();
export type RevenueManagerRow = z.infer<typeof RevenueManagerRowSchema>;

/**
 * @aiPurpose Comparison block for the dashboard. Discriminated union by
 *   `available` so the frontend narrows with one check instead of
 *   guarding every numeric field.
 *   - `available: true`  — full payload (total, deltas, category split)
 *     for the comparison window plus the same elapsed window from the
 *     selected month so the UI can render side-by-side numbers.
 *   - `available: false` — explicit no-data state. `reason`:
 *       `'sheet_missing'` — the comparison sheet was not discovered.
 *       `'no_data'`       — sheet exists but the elapsed window holds
 *                           zero usable rows.
 */
export const RevenueComparisonAvailableSchema = z
  .object({
    available: z.literal(true),
    month: RevenueMonthDescriptorSchema,
    selectedTotal: z.number().nonnegative(),
    comparisonTotal: z.number().nonnegative(),
    selectedCategories: RevenueCategoryBreakdownSchema,
    comparisonCategories: RevenueCategoryBreakdownSchema,
  })
  .strict();

export const RevenueComparisonUnavailableSchema = z
  .object({
    available: z.literal(false),
    reason: z.enum(['no_data', 'sheet_missing']),
    month: RevenueMonthDescriptorSchema.optional(),
  })
  .strict();

export const RevenueComparisonSchema = z.discriminatedUnion('available', [
  RevenueComparisonAvailableSchema,
  RevenueComparisonUnavailableSchema,
]);
export type RevenueComparison = z.infer<typeof RevenueComparisonSchema>;

export const RevenueCacheStateSchema = z.enum(['fresh', 'stale']);
export type RevenueCacheState = z.infer<typeof RevenueCacheStateSchema>;

/**
 * @aiPurpose Reason the cache served a stale fallback (or `null` when
 *   `cacheState === 'fresh'`). Carries operator-grade signal without
 *   leaking upstream payload content.
 * @aiInvariant Enum is closed — implementations MUST map every retryable
 *   error path to one of these tags rather than freeform strings.
 */
export const RevenueStaleReasonSchema = z.enum([
  'upstream_timeout',
  'upstream_5xx',
  'upstream_4xx',
  'parse_error',
  'network_error',
]);
export type RevenueStaleReason = z.infer<typeof RevenueStaleReasonSchema>;

export const RevenueFreshnessSchema = z
  .object({
    fetchedAt: z.string().datetime(),
    servedAt: z.string().datetime(),
    cacheState: RevenueCacheStateSchema,
    isStale: z.boolean(),
    staleReason: RevenueStaleReasonSchema.nullable(),
  })
  .strict();
export type RevenueFreshness = z.infer<typeof RevenueFreshnessSchema>;

export const RevenueSourceSchema = z
  .object({
    url: z.string().url(),
    label: z.string().min(1),
  })
  .strict();
export type RevenueSource = z.infer<typeof RevenueSourceSchema>;

/**
 * @aiPurpose Top-level response for `GET /api/finance/revenue`. Strict
 *   shape — extra keys never reach the client. `currency` is the
 *   literal `'RUB'` so the frontend formats with a known unit.
 *   `availableMonths` is the deterministic gviz-probe result in
 *   descending order; `selectedMonth` is one of them.
 *   `cumulativeDaily` is computed strictly from selected-month rows
 *   (calendar padding from adjacent months is filtered out upstream).
 */
export const RevenueDashboardSchema = z
  .object({
    currency: RevenueCurrencySchema,
    availableMonths: z.array(RevenueMonthDescriptorSchema).min(1),
    selectedMonth: RevenueMonthDescriptorSchema,
    totals: z.object({ total: z.number().nonnegative() }).strict(),
    categories: RevenueCategoryBreakdownSchema,
    cumulativeDaily: z.array(RevenueCumulativePointSchema),
    managers: z.array(RevenueManagerRowSchema),
    comparisons: z
      .object({
        previousMonth: RevenueComparisonSchema,
        previousYear: RevenueComparisonSchema,
      })
      .strict(),
    source: RevenueSourceSchema,
    freshness: RevenueFreshnessSchema,
  })
  .strict();
export type RevenueDashboard = z.infer<typeof RevenueDashboardSchema>;
